import React, { useState, useEffect } from "react";
import {
  TextInput,
  PasswordInput,
  Button,
  Paper,
  Title,
  Text,
  Loader,
  Center,
  Container,
  Stack,
} from "@mantine/core";
import { useSearchParams, useNavigate } from "react-router-dom";
import { resetPassword, validateResetToken } from "../../api";
import { useForm } from "@mantine/form";

export const ResetPassword = () => {
  const [error, setError] = useState("");
  const [isValidToken, setIsValidToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      password: "",
      confirmPassword: "",
      loading: false,
    },
    validate: {
      password: (value) => (value.length > 0 ? null : "Password is required"),
      confirmPassword: (value, values) =>
        value !== values.password ? "Passwords do not match" : null,
    },
  });

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const response = await validateResetToken(token);
        setIsValidToken(response.isValid);
        if (!response.isValid) {
          setError(
            "This password reset link is invalid, expired, or already used. Please request a new one.",
          );
        }
      } catch (err) {
        setError(
          "This password reset link is invalid, expired, or already used. Please request a new one.",
        );
        setIsValidToken(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkToken();
  }, [token, navigate]);

  const handleSubmit = async (values) => {
    if (!token || !isValidToken) {
      setError("Invalid reset token");
      return;
    }

    form.setFieldValue("loading", true);
    try {
      await resetPassword(token, values.password);
      navigate("/login?reset=success");
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "An error occurred. Please try again.";
      setError(errorMessage);
    } finally {
      form.setFieldValue("loading", false);
    }
  };

  if (isLoading) {
    return (
      <Center style={{ height: "100vh" }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <div
        style={{
          backgroundImage: "url('/static/socius hero.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          height: "85vh",
          width: "100%",
          position: "fixed",
          top: "68px",
          left: "0px",
          zIndex: -1,
        }}
      />

      <Container
        size="lg"
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4rem 1rem",
        }}
      >
        <Stack
          align="center"
          gap="xl"
          style={{
            position: "relative",
            width: "24rem",
          }}
        >
          <a href="/">
            <img
              src="/static/Socius_Full_DRK.svg"
              alt="Socius Legal"
              style={{
                height: "auto",
                width: "10rem",
                marginBottom: "2rem",
              }}
            />
          </a>

          <Paper p="xl" radius="md" style={{ width: "100%" }}>
            <Title order={2} ta="center" mb="xl" ff="DM Sans">
              Reset Your Password
            </Title>
            {!isValidToken ? (
              <Text c="red" size="sm" ta="center">
                {error}
              </Text>
            ) : (
              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                  <PasswordInput
                    placeholder="New Password"
                    {...form.getInputProps("password")}
                    required
                    styles={{ input: { textAlign: "center" } }}
                  />
                  <PasswordInput
                    placeholder="Confirm New Password"
                    {...form.getInputProps("confirmPassword")}
                    required
                    styles={{ input: { textAlign: "center" } }}
                  />
                  {error && (
                    <Text c="red" size="sm" ta="center">
                      {error}
                    </Text>
                  )}
                  <Button
                    type="submit"
                    loading={form.values.loading}
                    fullWidth
                    style={{
                      padding: "0.25rem 1rem",
                      fontSize: "1rem",
                      fontWeight: "700",
                    }}
                  >
                    Reset Password
                  </Button>
                </Stack>
              </form>
            )}
          </Paper>
        </Stack>
      </Container>

      <footer
        style={{
          width: "100%",
          padding: "1rem",
          backgroundColor: "rgba(255, 255, 255, 0.3)",
          textAlign: "center",
        }}
      >
        <Text size="sm" c="dimmed" style={{ fontFamily: "DM Sans" }}>
          © Socius Legal Corporation 2024
        </Text>
      </footer>
    </div>
  );
};
